import type { SeoPlatform } from '@rfz/contracts';
import { Button, Input } from '@rfz/ui';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Copy,
  FileText,
  LayoutGrid,
  ListChecks,
  Loader2,
  Sparkles,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  extractSeoProduct,
  generateSeoPrompt,
  type SeoProductExtractionResult,
  type SeoPromptGenerationResult,
  type SeoPromptKeywordContext,
} from './api.js';

interface SeoPromptDialogProps {
  platform: SeoPlatform | null;
  keywords: SeoPromptKeywordContext[];
  initialProductText?: string;
  onClose: () => void;
  variant?: 'dialog' | 'page';
}

export interface SeoPromptOpenDetail {
  platform: SeoPlatform | null;
  keywords: SeoPromptKeywordContext[];
  initialProductText: string;
}

export const OPEN_SEO_PROMPT_EVENT = 'rfz:open-seo-prompt';

export function SeoPromptLauncher({
  platform,
  keywords,
  initialProductText = '',
}: Pick<SeoPromptDialogProps, 'platform' | 'keywords' | 'initialProductText'>) {
  return (
    <Button
      variant="secondary"
      onClick={() =>
        window.dispatchEvent(
          new CustomEvent<SeoPromptOpenDetail>(OPEN_SEO_PROMPT_EVENT, {
            detail: { platform, keywords, initialProductText },
          }),
        )
      }
    >
      <Sparkles size={15} />
      生成 SEO Prompt
    </Button>
  );
}

type Product = SeoProductExtractionResult['product'];
type ProductTextKey = 'productNameCn' | 'productTypeCn' | 'quantity';
type ProductArrayKey = Exclude<keyof Product, ProductTextKey>;
type RiskKey = keyof SeoPromptGenerationResult['informationRisks'];
type ResultSection = 'overview' | 'core' | 'longTail' | 'optional' | 'rejected' | 'prompt';

const productTextFields: Array<{ key: ProductTextKey; label: string }> = [
  { key: 'productNameCn', label: '商品中文名称' },
  { key: 'productTypeCn', label: '商品类型' },
  { key: 'quantity', label: '数量' },
];

const productArrayFields: Array<{ key: ProductArrayKey; label: string }> = [
  { key: 'coreFunctions', label: '核心功能' },
  { key: 'features', label: '其他特性' },
  { key: 'materials', label: '材质' },
  { key: 'dimensions', label: '规格尺寸' },
  { key: 'includedItems', label: '包含物品或配件' },
  { key: 'useScenarios', label: '适用场景' },
  { key: 'targetUsers', label: '目标用户' },
  { key: 'sellingPoints', label: '明确卖点' },
];

const riskFields: Array<{ key: RiskKey; label: string }> = [
  { key: 'uncertain', label: '存疑信息' },
  { key: 'conflicting', label: '冲突信息' },
  { key: 'prohibitedClaims', label: '禁止虚构的信息' },
];

const stepLabels = ['商品资料', '确认信息', '关键词与 Prompt'];
const toLines = (values: string[]) => values.join('\n');
const fromLines = (value: string) =>
  value
    .split(/\r?\n/u)
    .map((item) => item.trim())
    .filter(Boolean);

export function SeoPromptDialog({
  platform,
  keywords,
  initialProductText = '',
  onClose,
  variant = 'dialog',
}: SeoPromptDialogProps) {
  const [selectedPlatform, setSelectedPlatform] = useState<SeoPlatform>(platform ?? 'wb');
  const [rawProductText, setRawProductText] = useState(initialProductText);
  const [extraction, setExtraction] = useState<SeoProductExtractionResult | null>(null);
  const [result, setResult] = useState<SeoPromptGenerationResult | null>(null);
  const [newColor, setNewColor] = useState('');
  const [copyState, setCopyState] = useState<'idle' | 'success' | 'error'>('idle');
  const [resultSection, setResultSection] = useState<ResultSection>('longTail');
  const [activeKeywordIndex, setActiveKeywordIndex] = useState(0);

  const extractionRequest = useMutation({
    mutationFn: () => extractSeoProduct({ rawProductText, platform: selectedPlatform }),
    onSuccess: (data) => {
      setExtraction(data);
      setResult(null);
      setCopyState('idle');
    },
  });

  const generation = useMutation({
    mutationFn: () => {
      if (!extraction) throw new Error('请先提取并确认商品信息');
      return generateSeoPrompt({
        confirmedProduct: extraction.product,
        informationRisks: extraction.informationRisks,
        platform: selectedPlatform,
        analysisContext: { keywords },
      });
    },
    onSuccess: (data) => {
      setResult(data);
      setResultSection(data.keywords.longTailKeywords.length ? 'longTail' : 'core');
      setActiveKeywordIndex(0);
      setCopyState('idle');
    },
  });

  const isPending = extractionRequest.isPending || generation.isPending;
  const activeStep = result ? 3 : extraction ? 2 : 1;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isPending) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPending, onClose]);

  const updateProductText = (key: ProductTextKey, value: string) =>
    setExtraction((current) =>
      current ? { ...current, product: { ...current.product, [key]: value } } : current,
    );
  const updateProductArray = (key: ProductArrayKey, value: string) =>
    setExtraction((current) =>
      current ? { ...current, product: { ...current.product, [key]: fromLines(value) } } : current,
    );
  const updateRisk = (key: RiskKey, value: string) =>
    setExtraction((current) =>
      current
        ? {
            ...current,
            informationRisks: { ...current.informationRisks, [key]: fromLines(value) },
          }
        : current,
    );

  const copyPrompt = async () => {
    if (!result?.gptPrompt) return;
    try {
      await navigator.clipboard.writeText(result.gptPrompt);
      setCopyState('success');
    } catch {
      setCopyState('error');
    }
  };

  const activeListLength = useMemo(() => {
    if (!result) return 0;
    if (resultSection === 'core') return result.keywords.coreKeywords.length;
    if (resultSection === 'longTail') return result.keywords.longTailKeywords.length;
    if (resultSection === 'optional') return result.keywords.optionalKeywords.length;
    if (resultSection === 'rejected') return result.keywords.rejectedKeywords.length;
    return 0;
  }, [result, resultSection]);

  const showItemPager = ['core', 'longTail', 'optional', 'rejected'].includes(resultSection);
  const moveActiveItem = (delta: number) => {
    if (!activeListLength) return;
    setActiveKeywordIndex((current) =>
      Math.min(activeListLength - 1, Math.max(0, current + delta)),
    );
  };

  const content = (
    <section
      className={`seo-prompt-dialog${variant === 'page' ? ' seo-prompt-dialog-page' : ''}${result ? ' is-result' : ''}`}
      role={variant === 'dialog' ? 'dialog' : 'region'}
      aria-modal={variant === 'dialog' ? true : undefined}
      aria-labelledby="seo-prompt-title"
      onMouseDown={variant === 'dialog' ? (event) => event.stopPropagation() : undefined}
    >
      <header className="seo-prompt-dialog-header">
        <div className="seo-prompt-title-group">
          <p className="eyebrow">SEO PROMPT</p>
          <div className="seo-prompt-title-line">
            <h3 id="seo-prompt-title">生成 SEO Prompt</h3>
            {result && (
              <span className="seo-result-complete">
                <CheckCircle2 size={14} /> 已完成
              </span>
            )}
          </div>
          <p>
            {result
              ? '关键词规划已生成，可逐项核对中俄含义后复制给 GPT。'
              : '整理商品事实与俄语关键词，生成可直接使用的任务说明。'}
          </p>
        </div>
        <button
          type="button"
          className={variant === 'page' ? 'seo-prompt-page-back' : undefined}
          aria-label={variant === 'page' ? '返回' : '关闭生成 SEO Prompt'}
          disabled={isPending}
          onClick={onClose}
        >
          {variant === 'page' ? (
            <>
              <ArrowLeft size={17} />
              <span>返回</span>
            </>
          ) : (
            <X size={19} />
          )}
        </button>
      </header>

      <nav className="seo-prompt-progress" aria-label="生成 SEO Prompt 进度">
        {stepLabels.map((label, index) => {
          const step = index + 1;
          const completed = step < activeStep;
          const active = step === activeStep;
          return (
            <div className={active ? 'is-active' : completed ? 'is-complete' : ''} key={label}>
              <span>{completed ? <Check size={14} /> : step}</span>
              <strong>{label}</strong>
            </div>
          );
        })}
      </nav>

      <div className={`seo-prompt-dialog-body${result ? ' is-result' : ''}`}>
        {activeStep === 1 && (
          <ProductSourceStep
            selectedPlatform={selectedPlatform}
            rawProductText={rawProductText}
            isPending={isPending}
            error={extractionRequest.isError ? extractionRequest.error.message : null}
            onPlatformChange={(value) => {
              setSelectedPlatform(value);
              setResult(null);
              setCopyState('idle');
            }}
            onRawProductTextChange={setRawProductText}
            onSubmit={() => extractionRequest.mutate()}
          />
        )}

        {activeStep === 2 && extraction && (
          <ProductConfirmationStep
            extraction={extraction}
            newColor={newColor}
            isPending={isPending}
            error={generation.isError ? generation.error.message : null}
            onProductTextChange={updateProductText}
            onProductArrayChange={updateProductArray}
            onRiskChange={updateRisk}
            onColorInputChange={setNewColor}
            onAddColor={() => {
              const color = newColor.trim();
              setExtraction((current) =>
                current && color && !current.product.colors.includes(color)
                  ? {
                      ...current,
                      product: {
                        ...current.product,
                        colors: [...current.product.colors, color],
                      },
                    }
                  : current,
              );
              setNewColor('');
            }}
            onRemoveColor={(color) =>
              setExtraction((current) =>
                current
                  ? {
                      ...current,
                      product: {
                        ...current.product,
                        colors: current.product.colors.filter((item) => item !== color),
                      },
                    }
                  : current,
              )
            }
            onBack={() => setExtraction(null)}
            onSubmit={() => generation.mutate()}
          />
        )}

        {activeStep === 3 && result && (
          <SeoResultWorkspace
            result={result}
            section={resultSection}
            activeIndex={activeKeywordIndex}
            onSectionChange={(section) => {
              setResultSection(section);
              setActiveKeywordIndex(0);
            }}
            onActiveIndexChange={setActiveKeywordIndex}
            onResultChange={setResult}
          />
        )}
      </div>

      {result && (
        <footer className="seo-prompt-dialog-footer">
          <Button variant="secondary" onClick={() => setResult(null)}>
            <ArrowLeft size={16} /> 返回上一步
          </Button>
          <div className="seo-result-pager">
            {showItemPager && activeListLength > 0 && (
              <>
                <Button
                  variant="secondary"
                  aria-label="上一个关键词"
                  disabled={activeKeywordIndex <= 0}
                  onClick={() => moveActiveItem(-1)}
                >
                  <ChevronLeft size={16} /> 上一个
                </Button>
                <strong>
                  {activeKeywordIndex + 1} / {activeListLength}
                </strong>
                <Button
                  variant="secondary"
                  aria-label="下一个关键词"
                  disabled={activeKeywordIndex >= activeListLength - 1}
                  onClick={() => moveActiveItem(1)}
                >
                  下一个 <ChevronRight size={16} />
                </Button>
              </>
            )}
          </div>
          <div className="seo-prompt-copy-row">
            {copyState === 'success' && <span className="seo-copy-success">已复制</span>}
            {copyState === 'error' && <span className="form-error">复制失败</span>}
            <Button disabled={!result.gptPrompt.trim()} onClick={() => void copyPrompt()}>
              <Copy size={16} /> 复制 GPT Prompt
            </Button>
          </div>
        </footer>
      )}
    </section>
  );

  if (variant === 'page') return <div className="seo-prompt-page">{content}</div>;

  return (
    <div className="seo-prompt-backdrop" role="presentation" onMouseDown={onClose}>
      {content}
    </div>
  );
}

function ProductSourceStep({
  selectedPlatform,
  rawProductText,
  isPending,
  error,
  onPlatformChange,
  onRawProductTextChange,
  onSubmit,
}: {
  selectedPlatform: SeoPlatform;
  rawProductText: string;
  isPending: boolean;
  error: string | null;
  onPlatformChange: (value: SeoPlatform) => void;
  onRawProductTextChange: (value: string) => void;
  onSubmit: () => void;
}) {
  return (
    <section className="seo-prompt-step" aria-labelledby="seo-product-source-title">
      <div className="seo-prompt-step-heading">
        <div>
          <h4 id="seo-product-source-title">粘贴商品资料</h4>
          <p>支持 1688 标题、供应商描述、商品参数、聊天记录和备注，格式可以混乱。</p>
        </div>
      </div>
      <label className="seo-prompt-platform-select">
        <span>目标平台</span>
        <select
          aria-label="目标平台"
          value={selectedPlatform}
          disabled={isPending}
          onChange={(event) => onPlatformChange(event.target.value as SeoPlatform)}
        >
          <option value="wb">Wildberries</option>
          <option value="ozon">Ozon</option>
        </select>
      </label>
      <textarea
        className="seo-prompt-source-input"
        aria-label="粘贴商品资料"
        value={rawProductText}
        disabled={isPending}
        placeholder="在这里粘贴完整商品资料…"
        onChange={(event) => onRawProductTextChange(event.target.value)}
      />
      {!rawProductText.trim() && <p className="form-error">请先粘贴商品资料后再生成。</p>}
      <Button
        className="seo-prompt-generate-button"
        disabled={!rawProductText.trim() || isPending}
        onClick={onSubmit}
      >
        {isPending ? <Loader2 className="spin" size={16} /> : <Sparkles size={16} />}
        {isPending ? 'DeepSeek 正在提取…' : '提取商品信息'}
      </Button>
      {error && (
        <p className="seo-prompt-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

function ProductConfirmationStep({
  extraction,
  newColor,
  isPending,
  error,
  onProductTextChange,
  onProductArrayChange,
  onRiskChange,
  onColorInputChange,
  onAddColor,
  onRemoveColor,
  onBack,
  onSubmit,
}: {
  extraction: SeoProductExtractionResult;
  newColor: string;
  isPending: boolean;
  error: string | null;
  onProductTextChange: (key: ProductTextKey, value: string) => void;
  onProductArrayChange: (key: ProductArrayKey, value: string) => void;
  onRiskChange: (key: RiskKey, value: string) => void;
  onColorInputChange: (value: string) => void;
  onAddColor: () => void;
  onRemoveColor: (color: string) => void;
  onBack: () => void;
  onSubmit: () => void;
}) {
  return (
    <section className="seo-prompt-step" aria-labelledby="seo-product-result-title">
      <div className="seo-prompt-step-heading">
        <div>
          <h4 id="seo-product-result-title">确认商品信息</h4>
          <p>检查提取结果并补充遗漏信息；数组字段每行一项。</p>
        </div>
      </div>
      <div className="seo-prompt-fields-grid">
        {productTextFields.map((field) => (
          <label key={field.key}>
            <span>{field.label}</span>
            <Input
              value={extraction.product[field.key]}
              onChange={(event) => onProductTextChange(field.key, event.target.value)}
            />
          </label>
        ))}
        {productArrayFields.map((field) => (
          <label key={field.key}>
            <span>{field.label}</span>
            <textarea
              value={toLines(extraction.product[field.key])}
              onChange={(event) => onProductArrayChange(field.key, event.target.value)}
            />
          </label>
        ))}
      </div>
      <div className="seo-color-editor">
        <span>颜色</span>
        <div className="seo-color-chips">
          {extraction.product.colors.map((color) => (
            <button
              type="button"
              key={color}
              aria-label={`移除颜色 ${color}`}
              onClick={() => onRemoveColor(color)}
            >
              {color} <X size={13} />
            </button>
          ))}
          {extraction.product.colors.length === 0 && <em>尚未识别颜色，请手动添加</em>}
        </div>
        <div className="seo-color-add">
          <Input
            aria-label="添加颜色"
            value={newColor}
            placeholder="输入具体颜色"
            onChange={(event) => onColorInputChange(event.target.value)}
          />
          <Button variant="secondary" disabled={!newColor.trim()} onClick={onAddColor}>
            添加颜色
          </Button>
        </div>
      </div>
      <div className="seo-prompt-risk-grid">
        {riskFields.map((field) => (
          <label key={field.key}>
            <span>{field.label}</span>
            <textarea
              value={toLines(extraction.informationRisks[field.key])}
              onChange={(event) => onRiskChange(field.key, event.target.value)}
            />
          </label>
        ))}
      </div>
      <div className="seo-confirm-actions">
        <Button variant="secondary" disabled={isPending} onClick={onBack}>
          <ArrowLeft size={16} /> 返回商品资料
        </Button>
        <Button disabled={isPending} onClick={onSubmit}>
          {isPending ? <Loader2 className="spin" size={16} /> : <Sparkles size={16} />}
          {isPending ? 'DeepSeek 正在联网规划…' : '确认并生成关键词 Prompt'}
        </Button>
      </div>
      {error && (
        <p className="seo-prompt-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

function SeoResultWorkspace({
  result,
  section,
  activeIndex,
  onSectionChange,
  onActiveIndexChange,
  onResultChange,
}: {
  result: SeoPromptGenerationResult;
  section: ResultSection;
  activeIndex: number;
  onSectionChange: (section: ResultSection) => void;
  onActiveIndexChange: (index: number) => void;
  onResultChange: (result: SeoPromptGenerationResult) => void;
}) {
  const navItems: Array<{
    key: ResultSection;
    label: string;
    count?: number;
    icon: typeof LayoutGrid;
  }> = [
    { key: 'overview', label: '概览', icon: LayoutGrid },
    {
      key: 'core',
      label: '核心关键词',
      count: result.keywords.coreKeywords.length,
      icon: ListChecks,
    },
    {
      key: 'longTail',
      label: '精准长尾词',
      count: result.keywords.longTailKeywords.length,
      icon: Sparkles,
    },
    {
      key: 'optional',
      label: '可选词',
      count: result.keywords.optionalKeywords.length,
      icon: CircleHelp,
    },
    { key: 'rejected', label: '排除词', count: result.keywords.rejectedKeywords.length, icon: X },
    { key: 'prompt', label: 'GPT Prompt', icon: FileText },
  ];

  return (
    <section className="seo-result-shell" aria-labelledby="seo-keywords-title">
      <h4 id="seo-keywords-title" className="sr-only">
        关键词和 GPT Prompt
      </h4>
      <div className="seo-result-summary">
        <div className="seo-primary-summary">
          <span>主要关键词（核心）</span>
          <strong>{result.keywords.primaryKeyword || '未生成'}</strong>
          <Input
            aria-label="主关键词中文翻译"
            value={result.keywords.primaryKeywordTranslationZh ?? ''}
            placeholder="补充中文翻译"
            onChange={(event) =>
              onResultChange({
                ...result,
                keywords: {
                  ...result.keywords,
                  primaryKeywordTranslationZh: event.target.value,
                },
              })
            }
          />
        </div>
        <SummaryMetric value={result.keywords.coreKeywords.length} label="核心关键词" />
        <SummaryMetric value={result.keywords.longTailKeywords.length} label="精准长尾词" />
        <div className="seo-summary-status">
          <CheckCircle2 size={22} />
          <strong>{result.webSearch.used ? 'Web 验证完成' : '已完成关键词规划'}</strong>
          <span>
            {result.webSearch.used
              ? `${result.webSearch.resultBlockCount} 个结果块`
              : '未调用联网搜索'}
          </span>
        </div>
      </div>

      <div className="seo-result-workspace">
        <aside className="seo-result-nav" aria-label="关键词结果分类">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                type="button"
                className={section === item.key ? 'is-active' : ''}
                key={item.key}
                onClick={() => onSectionChange(item.key)}
              >
                <Icon size={16} />
                <span>{item.label}</span>
                {item.count != null && <em>{item.count}</em>}
              </button>
            );
          })}
        </aside>

        <div className="seo-result-canvas">
          {section === 'overview' && <ResultOverview result={result} />}
          {section === 'core' && (
            <KeywordWorkbench
              title="核心关键词"
              items={result.keywords.coreKeywords}
              activeIndex={activeIndex}
              onActiveIndexChange={onActiveIndexChange}
              itemMeta={(item) =>
                item.heat == null ? '暂无热度' : `热度 ${item.heat.toLocaleString()}`
              }
              onChange={(items) =>
                onResultChange({
                  ...result,
                  keywords: { ...result.keywords, coreKeywords: items },
                })
              }
              renderDetails={(item, update) => (
                <>
                  <BilingualKeywordFields item={item} update={update} />
                  <DetailRow label="月均热度">
                    <strong>{item.heat == null ? '暂无数据' : item.heat.toLocaleString()}</strong>
                  </DetailRow>
                  <DetailTextarea
                    label="选择理由"
                    value={item.reason}
                    onChange={(value) => update({ reason: value })}
                  />
                </>
              )}
            />
          )}
          {section === 'longTail' && (
            <KeywordWorkbench
              title="精准长尾词"
              items={result.keywords.longTailKeywords}
              activeIndex={activeIndex}
              onActiveIndexChange={onActiveIndexChange}
              itemMeta={(item) => `${Math.round(item.confidence * 100)}% 置信度`}
              onChange={(items) =>
                onResultChange({
                  ...result,
                  keywords: { ...result.keywords, longTailKeywords: items },
                })
              }
              renderDetails={(item, update) => (
                <>
                  <BilingualKeywordFields item={item} update={update} />
                  <DetailRow label="来源类型">
                    <select
                      value={item.sourceType}
                      onChange={(event) =>
                        update({
                          sourceType: event.target.value as typeof item.sourceType,
                        })
                      }
                    >
                      <option value="analysis_keyword">分析关键词</option>
                      <option value="web_verified_synonym">联网核验同义词</option>
                      <option value="attribute_combination">属性组合</option>
                    </select>
                  </DetailRow>
                  <DetailTextarea
                    label="基于（每行一项）"
                    value={toLines(item.basedOn)}
                    onChange={(value) => update({ basedOn: fromLines(value) })}
                  />
                  <DetailRow label="置信度">
                    <div className="seo-confidence">
                      {Array.from({ length: 5 }, (_, index) => (
                        <i
                          className={index < Math.round(item.confidence * 5) ? 'is-filled' : ''}
                          key={index}
                        />
                      ))}
                      <strong>{Math.round(item.confidence * 100)}%</strong>
                    </div>
                  </DetailRow>
                  <DetailTextarea
                    label="简要理由"
                    value={item.reason}
                    onChange={(value) => update({ reason: value })}
                  />
                </>
              )}
            />
          )}
          {section === 'optional' && (
            <KeywordWorkbench
              title="可选词"
              items={result.keywords.optionalKeywords}
              activeIndex={activeIndex}
              onActiveIndexChange={onActiveIndexChange}
              itemMeta={(item) =>
                item.heat == null ? '暂无热度' : `热度 ${item.heat.toLocaleString()}`
              }
              onChange={(items) =>
                onResultChange({
                  ...result,
                  keywords: { ...result.keywords, optionalKeywords: items },
                })
              }
              renderDetails={(item, update) => (
                <>
                  <BilingualKeywordFields item={item} update={update} />
                  <DetailTextarea
                    label="选择理由"
                    value={item.reason}
                    onChange={(value) => update({ reason: value })}
                  />
                </>
              )}
            />
          )}
          {section === 'rejected' && (
            <KeywordWorkbench
              title="排除词"
              items={result.keywords.rejectedKeywords}
              activeIndex={activeIndex}
              onActiveIndexChange={onActiveIndexChange}
              itemMeta={() => '不建议使用'}
              onChange={(items) =>
                onResultChange({
                  ...result,
                  keywords: { ...result.keywords, rejectedKeywords: items },
                })
              }
              renderDetails={(item, update) => (
                <>
                  <BilingualKeywordFields item={item} update={update} />
                  <DetailTextarea
                    label="排除理由"
                    value={item.reason}
                    onChange={(value) => update({ reason: value })}
                  />
                </>
              )}
            />
          )}
          {section === 'prompt' && (
            <label className="seo-prompt-output">
              <div>
                <span>最终 GPT Prompt</span>
                <em>可在复制前直接编辑</em>
              </div>
              <textarea
                aria-label="GPT Prompt"
                value={result.gptPrompt}
                onChange={(event) => onResultChange({ ...result, gptPrompt: event.target.value })}
              />
            </label>
          )}
        </div>
      </div>
    </section>
  );
}

function SummaryMetric({ value, label }: { value: number; label: string }) {
  return (
    <div className="seo-summary-metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function ResultOverview({ result }: { result: SeoPromptGenerationResult }) {
  return (
    <div className="seo-result-overview">
      <div>
        <span>目标平台</span>
        <strong>{result.platform === 'wb' ? 'Wildberries' : 'Ozon'}</strong>
      </div>
      <div>
        <span>模板版本</span>
        <strong>{result.promptTemplateVersion}</strong>
      </div>
      <div>
        <span>商品名称</span>
        <strong>{result.product.productNameCn || result.product.productTypeCn || '未填写'}</strong>
      </div>
      <div>
        <span>联网核验</span>
        <strong>{result.webSearch.used ? '已完成' : '本次未使用'}</strong>
      </div>
      <p>从左侧选择关键词分类逐条核对，完成后使用底部按钮复制最终 GPT Prompt。</p>
    </div>
  );
}

interface EditableKeyword {
  keyword: string;
  translationZh?: string | undefined;
  reason: string;
}

function KeywordWorkbench<T extends EditableKeyword>({
  title,
  items,
  activeIndex,
  onActiveIndexChange,
  itemMeta,
  onChange,
  renderDetails,
}: {
  title: string;
  items: T[];
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  itemMeta: (item: T) => string;
  onChange: (items: T[]) => void;
  renderDetails: (item: T, update: (patch: Partial<T>) => void) => ReactNode;
}) {
  const safeIndex = Math.min(Math.max(0, activeIndex), Math.max(0, items.length - 1));
  const activeItem = items[safeIndex];
  const update = (patch: Partial<T>) =>
    onChange(items.map((item, index) => (index === safeIndex ? { ...item, ...patch } : item)));

  return (
    <div className="seo-keyword-workbench">
      <div className="seo-keyword-list-pane">
        <h5>{title}列表</h5>
        {items.length === 0 ? (
          <p className="seo-empty-keywords">暂无关键词</p>
        ) : (
          <div className="seo-keyword-list">
            {items.map((item, index) => (
              <button
                type="button"
                className={safeIndex === index ? 'is-active' : ''}
                key={`${index}:${item.keyword}`}
                onClick={() => onActiveIndexChange(index)}
              >
                <i />
                <span>
                  <strong>{item.keyword}</strong>
                  <em>{item.translationZh || '暂无中文翻译'}</em>
                  <small>{itemMeta(item)}</small>
                </span>
                <ChevronRight size={15} />
              </button>
            ))}
          </div>
        )}
        <strong className="seo-keyword-list-count">共 {items.length} 项</strong>
      </div>
      <div className="seo-keyword-detail-pane">
        <div className="seo-detail-heading">
          <div>
            <span>当前编辑</span>
            <h5>{title}</h5>
          </div>
          {activeItem && (
            <em>
              {safeIndex + 1} / {items.length}
            </em>
          )}
        </div>
        {activeItem ? (
          renderDetails(activeItem, update)
        ) : (
          <p className="seo-empty-keywords">暂无可编辑内容</p>
        )}
      </div>
    </div>
  );
}

function BilingualKeywordFields<T extends EditableKeyword>({
  item,
  update,
}: {
  item: T;
  update: (patch: Partial<T>) => void;
}) {
  return (
    <div className="seo-bilingual-fields">
      <label>
        <span>俄语关键词</span>
        <Input
          value={item.keyword}
          onChange={(event) => update({ keyword: event.target.value } as Partial<T>)}
        />
      </label>
      <label>
        <span>中文翻译</span>
        <Input
          value={item.translationZh ?? ''}
          placeholder="补充中文翻译"
          onChange={(event) => update({ translationZh: event.target.value } as Partial<T>)}
        />
      </label>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="seo-detail-row">
      <span>{label}</span>
      <div>{children}</div>
    </div>
  );
}

function DetailTextarea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="seo-detail-row seo-detail-textarea">
      <span>{label}</span>
      <textarea value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}
